import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';

export interface DespachoListItem {
  id: string;
  pedido_id: string;
  pedido_numero_orden: number | null;
  project_id: string;
  project_nombre: string;
  fecha_despacho: string;
  items_count: number;
  created_by: { id: string; nombre: string };
  created_at: string;
}

export interface DespachoItemDetail {
  id: string;
  pedido_item_id: string;
  material_nombre: string | null;
  material_sku: string | null;
  cantidad_despachada: number;
}

export interface DespachoDetail extends DespachoListItem {
  guia_transporte_url: string | null;
  notas: string | null;
  items: DespachoItemDetail[];
}

interface DespachoListResponse {
  data: DespachoListItem[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export function useDespachos(filters?: { projectId?: string; fechaDesde?: string; fechaHasta?: string; page?: number }) {
  return useQuery({
    queryKey: queryKeys.despachos.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.projectId) params.set('projectId', filters.projectId);
      if (filters?.fechaDesde) params.set('fechaDesde', filters.fechaDesde);
      if (filters?.fechaHasta) params.set('fechaHasta', filters.fechaHasta);
      if (filters?.page) params.set('page', String(filters.page));
      const qs = params.toString();
      const res = await api.get<DespachoListResponse>(`/despachos${qs ? `?${qs}` : ''}`);
      return res.data;
    },
  });
}

export function useDespachoDetail(despachoId: string) {
  return useQuery({
    queryKey: queryKeys.despachos.detail(despachoId),
    queryFn: async () => {
      const res = await api.get<{ data: DespachoDetail }>(`/despachos/${despachoId}`);
      return res.data.data;
    },
    enabled: !!despachoId,
  });
}

export function useCreateDespacho() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      pedidoId: string;
      data: {
        fechaDespacho: string;
        guiaTransporteS3Key?: string | null;
        notas?: string | null;
        items: Array<{ pedidoItemId: string; cantidadDespachada: number }>;
      };
    }) => {
      const res = await api.post<{ data: unknown }>(`/pedidos/${vars.pedidoId}/despachos`, vars.data);
      return res.data.data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.despachos.all });
      // Mark all pedido queries stale for when components mount
      queryClient.invalidateQueries({ queryKey: queryKeys.pedidos.all });
      // Immediately refetch the global list queries (used by DespachosPage) so
      // the pedido estado badge updates without a stale-data flash after navigation.
      queryClient.refetchQueries({ queryKey: ['pedidos', 'all'] });
      // Also force-refresh the specific pedido detail that was just dispatched
      queryClient.invalidateQueries({ queryKey: queryKeys.pedidos.detail(vars.pedidoId) });
    },
  });
}

export function useUpdateDespachoItems(despachoId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (items: Array<{ itemId: string; cantidadDespachada: number }>) => {
      const res = await api.put<{ data: DespachoDetail }>(`/despachos/${despachoId}/items`, { items });
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.despachos.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.pedidos.all });
      queryClient.refetchQueries({ queryKey: ['pedidos', 'all'] });
    },
  });
}
